import {CushlaGetQueryOptions, endpointKeys} from '@common/api'
import {
  ServicesReportResponse,
  PatientsWeeklyReportResponse,
} from '@common/models'
import {
  servicesReportNormaliser,
  patientsWeeklyReportNormaliser,
  partnerPatientReportNormaliser,
  partnerSlotHistoryListNormaliser,
} from '@common/normalisers'
import type {
  PartnerPatientReportResponse,
  PartnerPatientSlotHistoryResponse,
} from '@generated/models'

import {useApi, useWebGet, useWebFileDownload} from './helpers'

export const useGetPartnerServicesReport = (
  yymm: string,
  options?: CushlaGetQueryOptions<ServicesReportResponse>,
) =>
  useWebGet<ServicesReportResponse>(
    [endpointKeys.partnerAdminServicesReport, yymm],
    useApi().partnerAdminServicesReport({yymm}),
    servicesReportNormaliser,
    options,
  )

export const useGetPartnerPatientsReport = (
  yymmdd?: string,
  serviceId?: string,
  options?: CushlaGetQueryOptions<PatientsWeeklyReportResponse>,
) =>
  useWebGet<PatientsWeeklyReportResponse>(
    [endpointKeys.partnerAdminPatientsReport, yymmdd || '', serviceId || ''],
    useApi().partnerAdminPatientsReport({yymmdd, serviceId}),
    patientsWeeklyReportNormaliser,
    options,
  )

export const useGetPartnerPatientReport = (
  patientId: string,
  options?: CushlaGetQueryOptions<PartnerPatientReportResponse>,
) =>
  useWebGet<PartnerPatientReportResponse>(
    [endpointKeys.partnerAdminPatientReport, patientId],
    useApi().partnerAdminPatientReport({id: patientId}),
    partnerPatientReportNormaliser,
    options,
  )

export const useGetPartnerSlotReport = (
  slotId: string,
  options?: CushlaGetQueryOptions<PartnerPatientSlotHistoryResponse[]>,
) =>
  useWebGet<PartnerPatientSlotHistoryResponse[]>(
    [endpointKeys.partnerAdminSlotReport, slotId],
    useApi().partnerAdminSlotReport({id: slotId}),
    partnerSlotHistoryListNormaliser,
    options,
  )

export const useExportServicesReportPdf = (yymm: string) => {
  const api = useApi()

  return useWebFileDownload(
    [endpointKeys.partnerAdminServicesReportExportPdf, yymm],
    () => api.partnerAdminServicesReportExportPdf({yymm}),
    `services-report-${yymm}.pdf`,
  )
}

export const useExportServicesReportXlsx = () => {
  const api = useApi()

  return useWebFileDownload(
    [endpointKeys.partnerAdminServicesReportExportXlsx],
    () => api.partnerAdminServicesReportExportXlsx(),
    'services-report.xlsx',
  )
}

export const useExportPatientsReportPdf = (serviceId: string) => {
  const api = useApi()

  return useWebFileDownload(
    [endpointKeys.partnerAdminPatientsReportExportPdf, serviceId],
    () => {
      if (!serviceId) {
        throw new Error('Service ID is required')
      }
      return api.partnerAdminPatientsReportExportPdf({serviceId})
    },
    `patients-report-${serviceId}.pdf`,
  )
}

export const useExportPatientsReportXlsx = (serviceId: string) => {
  const api = useApi()

  return useWebFileDownload(
    [endpointKeys.partnerAdminPatientsReportExportXlsx, serviceId],
    () => {
      if (!serviceId) {
        throw new Error('Service ID is required')
      }
      return api.partnerAdminPatientsReportExportXlsx({serviceId})
    },
    `patients-report-${serviceId}.xlsx`,
  )
}
